import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { applyMotif } from "../lib/motif";
import { formatClock } from "../lib/time";
import { T, useI18n } from "../lib/i18n";
import { Trophy, Megaphone, Camera, Sparkles } from "lucide-react";
import Lightbox from "../components/Lightbox";
import SpeechCard from "../components/SpeechCard";

export default function Projector() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  useI18n();
  const [announcements, setAnnouncements] = useState([]);
  const [feed, setFeed] = useState([]);
  const [tick, setTick] = useState(0);
  const [whoWill, setWhoWill] = useState(null);
  const [presentation, setPresentation] = useState(null);

  const [activeMoment, setActiveMoment] = useState(null);
  const [queue, setQueue] = useState([]);
  const [schedule, setSchedule] = useState([]);
  // Phase 5B.1: track the 30s "moment" auto-clear timer so that
  // (a) overlapping moments never leave duplicate concurrent timers, and
  // (b) a pending timer is cleared on unmount (no setState after unmount).
  const momentTimerRef = useRef(null);
  useEffect(() => () => {
    if (momentTimerRef.current) clearTimeout(momentTimerRef.current);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [ev, ann, fd, q, sch, ww] = await Promise.all([
        api.get("/events/current"),
        api.get("/announcements"),
        api.get("/feed"),
        api.get("/queue"),
        api.get("/schedule"),
        api.get("/who-will/session"),
      ]);
      setEvent(ev.data);
      if (ev.data.motif) applyMotif(ev.data.motif);
      setAnnouncements(ann.data);
      setFeed(fd.data);
      setQueue(q.data);
      setSchedule(sch.data);
      setWhoWill(ww.data);
      try {
        const { data: pres } = await api.get("/presentation");
        setPresentation(pres);
      } catch (err) { console.debug("projector: presentation fetch failed", err); }
    } catch (err) { console.warn("projector: refresh failed", err); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "motif") applyMotif(msg.payload.motif);
    if (msg.type === "announcement") {
      setAnnouncements((a) => [msg.payload, ...a]);
      setTick((t) => t + 1);
    }
    if (msg.type === "moment") {
      if (msg.payload.projector !== false) {
        setActiveMoment(msg.payload);
        if (momentTimerRef.current) clearTimeout(momentTimerRef.current);
        momentTimerRef.current = setTimeout(() => setActiveMoment(null), 30000);
      }
    }
    if (msg.type === "queue_update") refresh();
    if (msg.type === "feed_new" || msg.type === "feed_update") refresh();
    if (msg.type === "presentation_update") setPresentation(msg.payload);
    if (msg.type === "whowill_update") setWhoWill(msg.payload?.session ? {
      ...msg.payload.session,
      current_question: msg.payload.current_question,
      couple: msg.payload.couple,
      tallies: msg.payload.tallies,
    } : whoWill);
  });

  // Cycle through panes
  const [pane, setPane] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPane((p) => (p + 1) % 4), 12000);
    return () => clearInterval(id);
  }, []);

  const queueInProgress = useMemo(() => queue.find((q) => q.status === "in_progress"), [queue]);
  const queueNext = useMemo(() => {
    const waiting = queue.filter((q) => q.status === "waiting" || q.status === "up_next")
      .sort((a, b) => (-(a.priority_level || 0) - -(b.priority_level || 0)) || a.joined_at.localeCompare(b.joined_at));
    return waiting[0] || null;
  }, [queue]);
  const nextSpeech = useMemo(() => schedule.find((s) => s.profile_attached), [schedule]);

  const [lbIndex, setLbIndex] = useState(-1);
  const photoPosts = useMemo(() => feed.filter((p) => p.image_url), [feed]);
  const lbItems = useMemo(
    () => photoPosts.map((p) => ({ src: fileUrl(p.image_url), caption: p.caption, author: p.author_name })),
    [photoPosts]
  );

  return (
    <div className="min-h-screen bg-motif text-motif relative overflow-hidden">
      {/* Subtle gold flourish */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary-motif opacity-80" />
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-motif opacity-80" />

      <header className="px-12 pt-10 pb-6 flex items-baseline justify-between">
        <div>
          <div className="text-xs tracking-luxe text-motif-muted">{T("tonight at")}</div>
          <div className="font-display text-6xl gold-shimmer">{event?.bride} & {event?.groom}</div>
          <div className="text-sm tracking-luxe text-motif-muted mt-1">{event?.venue}</div>
        </div>
        <div className="text-right">
          <div className="text-xs tracking-luxe text-motif-muted">{T("live")}</div>
          <div className="font-display text-3xl text-primary-motif">{formatClock(event?.timezone)}</div>
        </div>
      </header>

      <main className="px-12 pb-12 grid grid-cols-12 gap-8" style={{ minHeight: "calc(100vh - 240px)" }}>
        {/* Spotlight panel: priority = who-will > moment > photo queue > speech > announcement > welcome */}
        <section className="col-span-12 card-elev p-10 fadein">
          {whoWill && whoWill.state && !["NOT_STARTED", "COMPLETE"].includes(whoWill.state) && whoWill.current_question ? (
            <WhoWillSpotlight session={whoWill} />
          ) : activeMoment ? (
            <div className="fadein">
              <div className="flex items-center gap-2 text-sm tracking-luxe text-primary-motif mb-3">
                <Sparkles className="w-4 h-4" /> moment · {activeMoment.kind}
              </div>
              <div className="font-display text-[5vw] leading-[1.1]">{activeMoment.text}</div>
            </div>
          ) : queueInProgress ? (
            <div className="fadein text-center">
              <div className="flex items-center justify-center gap-2 text-sm tracking-luxe text-primary-motif mb-4">
                <Camera className="w-4 h-4" /> {T("now taking photos with")}
              </div>
              <div className="font-display text-[6vw] leading-tight gold-shimmer">
                {queueInProgress.guest_name}
              </div>
              <div className="text-2xl text-motif-muted mt-3 font-display">Table {queueInProgress.table_name}</div>
              {queueNext && (
                <div className="mt-10 pt-6 border-t border-motif">
                  <div className="text-xs tracking-luxe text-motif-muted mb-1">{T("up next")}</div>
                  <div className="font-display text-3xl text-motif">{queueNext.guest_name}</div>
                </div>
              )}
            </div>
          ) : announcements[0] ? (
            <div>
              <div className="flex items-center gap-2 text-sm tracking-luxe text-primary-motif mb-3">
                <Megaphone className="w-4 h-4" /> {T("announcement")}
              </div>
              <div key={tick} className="font-display text-[5vw] leading-[1.1] fadein">{announcements[0].text}</div>
            </div>
          ) : nextSpeech ? (
            <SpeechCard item={nextSpeech} large />
          ) : (
            <div className="flex items-center justify-center h-full text-motif-muted text-3xl font-display">{T("Welcome to the celebration")}</div>
          )}
        </section>

        {/* Live feed strip */}
        <section className="col-span-12 card-elev p-6">
          <div className="flex items-center gap-2 text-sm tracking-luxe text-primary-motif mb-3"><Camera className="w-4 h-4" /> {T("live feed")}</div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar">
            {photoPosts.slice(0, 12).map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setLbIndex(i)}
                className="flex-shrink-0 w-56 text-left transition hover:opacity-90"
                data-testid={`projector-feed-${p.id}`}
              >
                <img src={fileUrl(p.image_url)} alt="" className="w-56 h-40 object-cover rounded-xl" loading="lazy" />
                <div className="text-xs mt-1 truncate">{p.author_name}</div>
                {p.caption && <div className="text-[10px] text-motif-muted truncate">{p.caption}</div>}
              </button>
            ))}
            {photoPosts.length === 0 && (
              <div className="text-motif-muted text-sm py-8">Waiting for the first photo of the night…</div>
            )}
          </div>
        </section>
      </main>
      <Lightbox
        open={lbIndex >= 0}
        items={lbItems}
        index={lbIndex < 0 ? 0 : lbIndex}
        onClose={() => setLbIndex(-1)}
        onIndex={setLbIndex}
      />
      {presentation?.state === "CROSSWORD_CHAMPION_REVEAL" && presentation.payload && (
        <WinnerSpotlightOverlay payload={presentation.payload} />
      )}
    </div>
  );
}

function fmtSpotSecs(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function WinnerSpotlightOverlay({ payload }) {
  const confetti = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        left: (i * 37 + 11) % 100,
        delay: (i % 8) * 0.5,
        dur: 4.5 + (i % 5) * 0.9,
        size: 7 + (i % 3) * 5,
        gold: i % 3 !== 0,
        spin: i % 2 === 0 ? 1 : -1,
      })),
    []
  );
  return (
    <div className="fixed inset-0 z-50 overflow-hidden spotlight-backdrop" data-testid="projector-winner-spotlight">
      {/* Gold radial glow */}
      <div className="absolute inset-0 spotlight-glow" />
      {/* Confetti */}
      {confetti.map((c) => (
        <span
          key={`c-${c.left}`}
          className="spotlight-confetti"
          style={{
            left: `${c.left}%`,
            width: c.size,
            height: c.size * 0.45,
            animationDelay: `${c.delay}s`,
            animationDuration: `${c.dur}s`,
            background: c.gold ? "var(--motif-primary, #c2a14d)" : "#f6efe1",
            transform: `rotate(${c.spin * 25}deg)`,
          }}
        />
      ))}
      <div className="relative h-full flex flex-col items-center justify-center text-center px-10">
        <div className="spotlight-rise flex items-center gap-3 text-sm tracking-luxe" style={{ color: "#d9c489", animationDelay: "0.1s" }}>
          <Sparkles className="w-5 h-5" /> crossword champion
        </div>
        <div className="spotlight-pop mt-6">
          <Trophy className="w-20 h-20 mx-auto" style={{ color: "#e3c96f" }} />
        </div>
        <div className="spotlight-pop font-display gold-shimmer leading-tight mt-4" style={{ fontSize: "8vw", animationDelay: "0.25s" }}>
          {payload.display_name}
        </div>
        {payload.table_name && (
          <div className="spotlight-rise font-display text-3xl mt-2" style={{ color: "#cbb26a", animationDelay: "0.5s" }}>
            Table {String(payload.table_name).replace(/^table\s*/i, "")}
          </div>
        )}
        <div className="spotlight-rise flex items-center gap-10 mt-10 flex-wrap justify-center" style={{ animationDelay: "0.7s" }}>
          <div>
            <div className="text-[11px] tracking-luxe" style={{ color: "#9d8b57" }}>FINAL SCORE</div>
            <div className="font-display text-5xl tabular-nums" style={{ color: "#f0e3bb" }}>{(payload.final_score ?? 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[11px] tracking-luxe" style={{ color: "#9d8b57" }}>CORRECT</div>
            <div className="font-display text-5xl tabular-nums" style={{ color: "#f0e3bb" }}>{payload.correct_clue_count}/{payload.total_clue_count}</div>
          </div>
          <div>
            <div className="text-[11px] tracking-luxe" style={{ color: "#9d8b57" }}>STATUS</div>
            <div className="font-display text-5xl" style={{ color: "#f0e3bb" }}>{payload.submission_type === "COMPLETE" ? "Complete" : "Partial"}</div>
          </div>
          <div>
            <div className="text-[11px] tracking-luxe" style={{ color: "#9d8b57" }}>TIME</div>
            <div className="font-display text-5xl tabular-nums" style={{ color: "#f0e3bb" }}>{fmtSpotSecs(payload.elapsed_seconds)}</div>
          </div>
        </div>
        <div className="spotlight-rise font-display text-4xl mt-12" style={{ color: "#f0e3bb", animationDelay: "1s" }}>
          Congratulations!
        </div>
      </div>
    </div>
  );
}

function WhoWillSpotlight({ session }) {
  const q = session.current_question;
  const couple = session.couple || { aimee: "AIMEE", riyaz: "RIYAZ" };
  const t = session.tallies || { aimee: 0, riyaz: 0, total: 0 };
  const showLive = true; // Reveal step removed — live results always visible
  const aimeePct = t.total ? (t.aimee / t.total) * 100 : 0;
  const riyazPct = t.total ? (t.riyaz / t.total) * 100 : 0;
  const stateLabel =
    session.state === "READY" ? "Get ready…" :
    session.state === "VOTING_OPEN" ? "VOTING OPEN" :
    session.state === "VOTING_CLOSED" ? "Voting Closed" : session.state;

  return (
    <div className="fadein">
      <div className="flex items-center gap-2 text-sm tracking-luxe text-primary-motif mb-3">
        <Sparkles className="w-4 h-4" /> who will · {stateLabel.toLowerCase()}
      </div>
      <div className="font-display text-[4vw] leading-[1.1] mb-6">{q?.text}</div>

      {/* Big donut using conic-gradient */}
      <div className="flex items-center gap-8">
        <div
          className="w-56 h-56 rounded-full flex-shrink-0"
          style={{
            background: showLive && t.total
              ? `conic-gradient(var(--motif-primary) 0 ${aimeePct}%, #6b7280 ${aimeePct}% 100%)`
              : "conic-gradient(#e5e7eb 0 100%)",
          }}
          aria-label="Vote breakdown chart"
        >
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-40 h-40 rounded-full bg-motif flex items-center justify-center">
              <div className="text-center">
                <div className="text-[10px] tracking-luxe text-motif-muted">TOTAL</div>
                <div className="font-display text-4xl text-motif tabular-nums">{t.total}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 gap-4">
          <div className="p-5 rounded-2xl bg-accent-motif flex items-center justify-between">
            <div>
              <div className="text-xs tracking-luxe text-motif-muted">A</div>
              <div className="font-display text-4xl">{couple.aimee}</div>
            </div>
            {showLive && (
              <div className="text-right">
                <div className="font-display text-4xl text-primary-motif tabular-nums">{Math.round(aimeePct)}%</div>
                <div className="text-sm text-motif-muted tabular-nums">{t.aimee} votes</div>
              </div>
            )}
          </div>
          <div className="p-5 rounded-2xl bg-accent-motif flex items-center justify-between">
            <div>
              <div className="text-xs tracking-luxe text-motif-muted">B</div>
              <div className="font-display text-4xl">{couple.riyaz}</div>
            </div>
            {showLive && (
              <div className="text-right">
                <div className="font-display text-4xl text-primary-motif tabular-nums">{Math.round(riyazPct)}%</div>
                <div className="text-sm text-motif-muted tabular-nums">{t.riyaz} votes</div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
